/**
 * smarq-shop.js
 * Behaviour for the Smarq collection, search, product and page templates.
 *   - results (collection / search): filters, sort and pagination re-render the
 *     section through the Section Rendering API and update the URL
 *   - product: gallery (scroll-snap stage, thumbs, keyboard, hover zoom),
 *     option pills -> variant, quantity stepper, add to cart, sticky mobile bar
 *   - cart: posts to /cart/add.js and hands the result to Horizon's existing
 *     cart drawer through the theme's `cart:update` event (no second cart)
 *   - quick add on product cards
 *   - "pairs well with": swaps in Shopify recommendations when available
 *   - FAQ page: h3 blocks -> native <details> accordions at render time
 * Deferred, dependency-free. Motion respects prefers-reduced-motion and
 * reuses smarq-motion.js for reveals on re-rendered markup.
 */
(function () {
  'use strict';
  if (window.SmarqShop) return;

  var root = document.documentElement;
  root.classList.add('sq-js');
  var reduced = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  var finePointer = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)').matches : false;

  function $(sel, scope) { return (scope || document).querySelector(sel); }
  function $$(sel, scope) { return Array.prototype.slice.call((scope || document).querySelectorAll(sel)); }
  function refreshMotion(scope) { if (window.SmarqMotion && window.SmarqMotion.refresh) window.SmarqMotion.refresh(scope); }
  function shopRoot() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    format = format || '${{amount}}';
    var re = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(re);
    var key = match ? match[1] : 'amount';
    function fmt(number, precision, thousands, decimal) {
      var n = (Number(number) / 100).toFixed(precision);
      var parts = n.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }
    var value;
    switch (key) {
      case 'amount_no_decimals': value = fmt(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' ', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      default: value = fmt(cents, 2, ',', '.');
    }
    return format.replace(re, value);
  }

  /* ---------- Cart: add, then hand off to Horizon's drawer ---------- */
  function cartSectionId() {
    var el = document.querySelector('cart-items-component');
    return el && el.dataset.sectionId ? el.dataset.sectionId : null;
  }

  function addToCart(variantId, quantity) {
    var body = { id: Number(variantId), quantity: Math.max(1, Number(quantity) || 1) };
    var sid = cartSectionId();
    if (sid) {
      body.sections = sid;
      body.sections_url = window.location.pathname;
    }
    return fetch(shopRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || data.status) {
            var err = new Error(data.description || data.message || 'Could not add this to your cart.');
            err.data = data;
            throw err;
          }
          return data;
        });
      })
      .then(function (data) {
        announceCart(data, variantId, body.quantity);
        return data;
      });
  }

  /**
   * Horizon's cart-icon and cart-items-component both listen for `cart:update`
   * on document and read event.detail.data (itemCount, source, sections).
   * The drawer itself auto-opens on that event only when it has `auto-open`;
   * otherwise we call its open() method once the items have morphed.
   */
  function announceCart(data, variantId, quantity) {
    var event = new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: data,
        sourceId: 'smarq-shop',
        data: {
          source: 'product-form-component',
          itemCount: quantity,
          productId: data && data.product_id ? String(data.product_id) : undefined,
          variantId: String(variantId),
          sections: data && data.sections ? data.sections : undefined
        }
      }
    });
    document.dispatchEvent(event);

    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer || drawer.hasAttribute('auto-open')) return;
    var open = function () {
      var el = document.querySelector('cart-drawer-component');
      if (el && typeof el.open === 'function') el.open();
    };
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('cart-drawer-component').then(function () { setTimeout(open, 80); });
    } else {
      setTimeout(open, 80);
    }
  }

  /* ---------- Quick add on cards (delegated) ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-sq-quick-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    var text = btn.querySelector('.sq-pcard__quick-text');
    var original = text ? text.textContent : '';
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    addToCart(btn.getAttribute('data-sq-quick-add'), 1)
      .then(function () {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.classList.add('is-added');
        if (text) text.textContent = 'Added';
        setTimeout(function () {
          btn.classList.remove('is-added');
          if (text) text.textContent = original;
        }, 2000);
      })
      .catch(function () {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        if (text) text.textContent = 'Try again';
        setTimeout(function () { if (text) text.textContent = original; }, 2500);
      });
  });

  /* ---------- Results: filters, sort, pagination ---------- */
  function initResults(section) {
    if (!section || section.hasAttribute('data-sq-ready')) return;
    section.setAttribute('data-sq-ready', '');
    var sectionId = section.getAttribute('data-section-id');
    var host = section.closest('.shopify-section') || section.parentNode;
    if (!sectionId || !host) return;

    function load(url, push, scrollToTop) {
      var fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);
      section.setAttribute('aria-busy', 'true');
      fetch(fetchUrl.toString(), { headers: { Accept: 'text/html' } })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.getElementById('shopify-section-' + sectionId);
          host.innerHTML = fresh ? fresh.innerHTML : doc.body.innerHTML;
          if (push && window.history && history.pushState) history.pushState({ sq: sectionId }, '', url);
          if (scrollToTop) {
            var top = host.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top: Math.max(0, top), behavior: reduced ? 'auto' : 'smooth' });
          }
          refreshMotion(host);
          init(host);
        })
        .catch(function () { window.location.href = url; });
    }
    host._sqLoad = load;

    var form = $('[data-sq-facets]', section);
    if (form) {
      var timer = null;
      function submit() {
        var params = new URLSearchParams(new FormData(form));
        var empty = [];
        params.forEach(function (value, key) { if (value === '') empty.push(key); });
        empty.forEach(function (key) { params.delete(key); });
        var query = params.toString();
        var url = form.getAttribute('action') + (query ? '?' + query : '');
        $$('[data-sq-facet][open]', form).forEach(function (d) { d.removeAttribute('open'); });
        load(url, true, false);
      }
      form.addEventListener('change', function (e) {
        if (e.target.matches('input[type="number"]')) return; // price applies on submit / Apply
        clearTimeout(timer);
        timer = setTimeout(submit, 150);
      });
      form.addEventListener('submit', function (e) { e.preventDefault(); submit(); });
    }

    section.addEventListener('click', function (e) {
      var link = e.target.closest('[data-sq-pagination] a, .sq-facets__active a, .sq-facet__reset');
      if (!link) return;
      e.preventDefault();
      load(link.href, true, !!link.closest('[data-sq-pagination]'));
    });
  }

  // One document-level closer for facet dropdowns
  document.addEventListener('click', function (e) {
    $$('[data-sq-facet][open]').forEach(function (d) { if (!d.contains(e.target)) d.removeAttribute('open'); });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    $$('[data-sq-facet][open]').forEach(function (d) {
      d.removeAttribute('open');
      var s = d.querySelector('summary');
      if (s) s.focus();
    });
  });
  window.addEventListener('popstate', function () {
    var host = $('[data-sq-results]');
    host = host && (host.closest('.shopify-section') || host.parentNode);
    if (host && host._sqLoad) host._sqLoad(window.location.href, false, false);
  });

  /* ---------- Product gallery ---------- */
  function initGallery(el) {
    if (!el) return null;
    var stage = $('[data-sq-stage]', el);
    var slides = $$('[data-sq-slide]', el);
    var thumbs = $$('[data-sq-thumb]', el);
    var thumbRail = $('[data-sq-thumbs]', el);
    var counter = $('[data-sq-current]', el);
    var prev = $('[data-sq-prev]', el);
    var next = $('[data-sq-next]', el);
    var index = 0;
    if (!stage || !slides.length) return null;

    function setActive(i) {
      index = i;
      thumbs.forEach(function (t, j) {
        var on = j === i;
        t.classList.toggle('is-active', on);
        if (on) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
        t.tabIndex = on ? 0 : -1;
      });
      if (counter) counter.textContent = String(i + 1);
      var t = thumbs[i];
      if (t && thumbRail) {
        thumbRail.scrollTo({
          left: t.offsetLeft - thumbRail.clientWidth / 2 + t.clientWidth / 2,
          top: t.offsetTop - thumbRail.clientHeight / 2 + t.clientHeight / 2,
          behavior: reduced ? 'auto' : 'smooth'
        });
      }
    }
    function goTo(i, instant) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      stage.scrollTo({ left: slides[i].offsetLeft, behavior: instant || reduced ? 'auto' : 'smooth' });
      setActive(i);
    }
    function goToMedia(id, instant) {
      var i = -1;
      slides.some(function (s, j) { if (s.getAttribute('data-media-id') === String(id)) { i = j; return true; } return false; });
      if (i >= 0 && i !== index) goTo(i, instant);
    }

    var ticking = false;
    stage.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        var i = Math.round(stage.scrollLeft / Math.max(1, stage.clientWidth));
        if (i !== index && slides[i]) setActive(i);
      });
    }, { passive: true });

    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { goTo(i); });
      t.addEventListener('keydown', function (e) {
        var d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        var n = (i + d + thumbs.length) % thumbs.length;
        goTo(n);
        thumbs[n].focus();
      });
    });
    stage.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
    });
    if (prev) prev.addEventListener('click', function () { goTo(index - 1); });
    if (next) next.addEventListener('click', function () { goTo(index + 1); });

    if (!reduced && finePointer) {
      slides.forEach(function (slide) {
        var img = slide.querySelector('[data-sq-zoom]');
        if (!img) return;
        slide.addEventListener('pointerenter', function () { slide.classList.add('is-zoom'); });
        slide.addEventListener('pointerleave', function () { slide.classList.remove('is-zoom'); });
        slide.addEventListener('pointermove', function (e) {
          var r = slide.getBoundingClientRect();
          img.style.setProperty('--zx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
          img.style.setProperty('--zy', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%');
        }, { passive: true });
      });
    }
    window.addEventListener('resize', function () { stage.scrollTo({ left: slides[index].offsetLeft }); }, { passive: true });

    return { goTo: goTo, goToMedia: goToMedia };
  }

  /* ---------- Product page ---------- */
  function initProduct(section) {
    if (!section || section.hasAttribute('data-sq-ready')) return;
    section.setAttribute('data-sq-ready', '');

    var variants = [];
    var variantsEl = $('[data-sq-variants]', section);
    try { variants = JSON.parse(variantsEl ? variantsEl.textContent : '[]'); } catch (err) { variants = []; }
    var moneyFormat = section.getAttribute('data-money-format') || '';

    var form = $('[data-sq-atc]', section);
    var idInput = $('[data-sq-variant-id]', section);
    var atcBtn = $('[data-sq-atc-btn]', section);
    var atcLabel = $('[data-sq-atc-label]', section);
    var errorEl = $('[data-sq-atc-error]', section);
    var liveEl = $('[data-sq-atc-live]', section);
    var express = $('[data-sq-express]', section);
    var priceCur = $('[data-sq-price-current]', section);
    var priceCmp = $('[data-sq-price-compare]', section);
    var priceSave = $('[data-sq-price-save]', section);
    var priceSavePct = $('[data-sq-price-save-pct]', section);
    var stock = $('[data-sq-stock]', section);
    var sticky = $('[data-sq-sticky]', section);
    var stickyPrice = $('[data-sq-sticky-price]', section);
    var stickyBtn = $('[data-sq-sticky-btn]', section);
    var stickyImg = $('[data-sq-sticky-img]', section);
    var qtyInput = $('[data-sq-qty-input]', section);
    var optionSets = $$('[data-sq-option]', section);
    var gallery = initGallery($('[data-sq-gallery]', section));

    function currentSelection() {
      return optionSets.map(function (set) {
        var c = set.querySelector('[data-sq-option-input]:checked');
        return c ? c.value : null;
      });
    }
    function findVariant(sel) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var ok = sel.every(function (val, j) { return v['option' + (j + 1)] === val; });
        if (ok) return v;
      }
      return null;
    }
    function markAvailability(sel) {
      optionSets.forEach(function (set, i) {
        $$('[data-sq-option-input]', set).forEach(function (input) {
          var probe = sel.slice();
          probe[i] = input.value;
          var exists = variants.some(function (v) {
            return v.available && probe.every(function (val, j) { return val === null || v['option' + (j + 1)] === val; });
          });
          var label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !exists);
        });
        var valueEl = set.querySelector('[data-sq-option-value]');
        var checked = set.querySelector('[data-sq-option-input]:checked');
        if (valueEl && checked) valueEl.textContent = checked.value;
      });
    }
    function labelFor(variant, available) {
      if (!variant) return 'Unavailable';
      return available ? 'Add to cart' : 'Sold out';
    }
    function render(variant) {
      var available = !!(variant && variant.available);
      if (idInput && variant) idInput.value = variant.id;
      if (variant && priceCur) {
        var onSale = !!(variant.compare_at_price && variant.compare_at_price > variant.price);
        priceCur.textContent = formatMoney(variant.price, moneyFormat);
        priceCur.classList.toggle('is-sale', onSale);
        if (priceCmp) {
          priceCmp.hidden = !onSale;
          if (onSale) priceCmp.innerHTML = '<span class="visually-hidden">Regular price </span>' + formatMoney(variant.compare_at_price, moneyFormat);
        }
        if (priceSave) {
          var pct = onSale ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;
          priceSave.hidden = !(onSale && pct > 0);
          if (priceSavePct) priceSavePct.textContent = String(pct);
        }
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, moneyFormat);
      }
      if (stock) {
        stock.textContent = variant ? (available ? 'In stock' : 'Sold out') : 'Unavailable';
        stock.classList.toggle('is-out', !available);
      }
      if (atcBtn) { atcBtn.disabled = !available; if (atcLabel) atcLabel.textContent = labelFor(variant, available); }
      if (stickyBtn) { stickyBtn.disabled = !available; stickyBtn.textContent = labelFor(variant, available); }
      if (express) express.hidden = !available;
      if (variant && variant.featured_media && gallery) gallery.goToMedia(variant.featured_media.id);
      if (variant && variant.featured_image && stickyImg && variant.featured_image.src) {
        stickyImg.src = variant.featured_image.src.replace(/(\.[a-z]+)(\?.*)?$/i, '_120x120$1$2');
      }
      if (variant && window.history && history.replaceState) {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState(history.state, '', url.toString());
        } catch (err) { /* no-op */ }
      }
    }

    if (optionSets.length) {
      markAvailability(currentSelection());
      section.addEventListener('change', function (e) {
        if (!e.target.matches('[data-sq-option-input]')) return;
        var sel = currentSelection();
        markAvailability(sel);
        render(findVariant(sel));
      });
    }
    var featured = section.getAttribute('data-featured-media');
    if (featured && gallery) gallery.goToMedia(featured, true);

    section.addEventListener('click', function (e) {
      var dec = e.target.closest('[data-sq-qty-dec]');
      var inc = e.target.closest('[data-sq-qty-inc]');
      if (!qtyInput || (!dec && !inc)) return;
      var v = parseInt(qtyInput.value, 10) || 1;
      qtyInput.value = dec ? Math.max(1, v - 1) : Math.min(99, v + 1);
    });

    function showError(msg) {
      if (!errorEl) return;
      if (!msg) { errorEl.hidden = true; errorEl.textContent = ''; return; }
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!atcBtn || atcBtn.disabled) return;
        var variantId = idInput ? idInput.value : null;
        if (!variantId) { showError('Choose an option first.'); return; }
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        showError(null);
        atcBtn.classList.add('is-loading');
        atcBtn.setAttribute('aria-busy', 'true');
        addToCart(variantId, qty)
          .then(function () {
            atcBtn.classList.remove('is-loading');
            atcBtn.removeAttribute('aria-busy');
            atcBtn.classList.add('is-added');
            if (liveEl) liveEl.textContent = 'Added to cart';
            setTimeout(function () { atcBtn.classList.remove('is-added'); if (liveEl) liveEl.textContent = ''; }, 2200);
          })
          .catch(function (err) {
            atcBtn.classList.remove('is-loading');
            atcBtn.removeAttribute('aria-busy');
            showError(err && err.message ? err.message : 'Could not add this to your cart. Please try again.');
          });
      });
    }

    if (sticky && atcBtn && 'IntersectionObserver' in window) {
      sticky.hidden = false;
      var io = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        var past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', past);
        sticky.setAttribute('aria-hidden', past ? 'false' : 'true');
        if (stickyBtn) stickyBtn.tabIndex = past ? 0 : -1;
        document.body.classList.toggle('sq-sticky-open', past);
      }, { threshold: 0 });
      io.observe(atcBtn);
    }
  }

  /* ---------- Pairs well with ---------- */
  function initPairs(section) {
    if (!section || section.hasAttribute('data-sq-ready')) return;
    section.setAttribute('data-sq-ready', '');
    var url = section.getAttribute('data-url');
    var productId = section.getAttribute('data-product-id');
    if (!url || !productId) return;
    fetch(url + '&product_id=' + encodeURIComponent(productId), { headers: { Accept: 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var list = doc.querySelector('[data-sq-pairs-list]');
        var fresh = doc.querySelector('[data-sq-pairs] > .sq-container');
        var container = section.querySelector(':scope > .sq-container');
        if (list && Number(list.getAttribute('data-count')) > 0 && fresh && container) {
          container.innerHTML = fresh.innerHTML;
          section.setAttribute('data-source', 'recommendations');
          refreshMotion(section);
        }
      })
      .catch(function () { /* keep the fallback */ });
  }

  /* ---------- FAQ accordions ---------- */
  function initFaq(el) {
    if (!el || el.hasAttribute('data-sq-ready')) return;
    el.setAttribute('data-sq-ready', '');
    var headings = $$('h3', el);
    if (!headings.length) return;
    headings.forEach(function (h) {
      var nodes = [];
      var n = h.nextSibling;
      while (n && !(n.nodeType === 1 && /^H[1-3]$/.test(n.tagName))) { nodes.push(n); n = n.nextSibling; }
      var details = document.createElement('details');
      details.className = 'sq-faq__item';
      var summary = document.createElement('summary');
      summary.className = 'sq-faq__summary';
      summary.textContent = h.textContent.trim();
      var body = document.createElement('div');
      body.className = 'sq-faq__body';
      details.appendChild(summary);
      details.appendChild(body);
      var wrap = h.previousElementSibling;
      if (!(wrap && wrap.classList && wrap.classList.contains('sq-faq'))) {
        wrap = document.createElement('div');
        wrap.className = 'sq-faq';
        h.parentNode.insertBefore(wrap, h);
      }
      nodes.forEach(function (node) { body.appendChild(node); });
      wrap.appendChild(details);
      h.parentNode.removeChild(h);
    });
  }

  /* ---------- Boot ---------- */
  function init(scope) {
    scope = scope || document;
    $$('[data-sq-product]', scope).forEach(initProduct);
    $$('[data-sq-results]', scope).forEach(initResults);
    $$('[data-sq-pairs]', scope).forEach(initPairs);
    $$('[data-sq-faq]', scope).forEach(initFaq);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  window.SmarqShop = { init: init, addToCart: addToCart, formatMoney: formatMoney };
})();
